import { supabase } from "@/lib/supabase";
import type { BoundJobSpec, JobSpecPhaseName, RequiredPhotoType } from "@/data/jobSpec";
import { resolveBoundSpec } from "@/data/jobSpec";
import {
  ensureExecution,
  qaFailPhase,
  qaPassPhase,
  recordPhoto,
  submitPhase,
  toggleChecklist,
  type JobSpecExecution,
} from "@/data/jobSpecExecution";
import type { MaterialKit } from "@/data/materialKit";
import { uploadAttachment } from "@/services/attachment.service";

export interface UploadProofInput {
  jobId: string;
  phaseName: JobSpecPhaseName;
  photoType: RequiredPhotoType;
  file?: File;
  actor: string;
  bound?: BoundJobSpec;
}

export interface ToggleCheckInput {
  jobId: string;
  phaseName: JobSpecPhaseName;
  itemId: string;
  checked: boolean;
  actor: string;
  bound?: BoundJobSpec;
}

export interface SubmitPhaseInput {
  jobId: string;
  phaseName: JobSpecPhaseName;
  actor: string;
  at?: Date;
  bound?: BoundJobSpec;
}

export interface ExecutionRepository {
  // Read
  getExecution(jobId: string, bound?: BoundJobSpec): Promise<JobSpecExecution | null>;

  // Write
  toggleChecklist(input: ToggleCheckInput): Promise<JobSpecExecution>;
  uploadRequiredPhoto(input: UploadProofInput): Promise<JobSpecExecution>;
  submitPhase(input: SubmitPhaseInput): Promise<JobSpecExecution>;

  // QA
  qaPass(jobId: string, phaseName: JobSpecPhaseName, actor: string): Promise<JobSpecExecution>;
  qaFail(jobId: string, phaseName: JobSpecPhaseName, actor: string, reason: string): Promise<JobSpecExecution>;
}

interface RepositoryOptions {
  getCachedExecution: (jobId: string) => JobSpecExecution | undefined;
  setCachedExecution: (execution: JobSpecExecution) => void;
  getMaterialKit?: (jobId: string) => MaterialKit | undefined;
}

const READ_TABLES_PRIMARY = import.meta.env.VITE_EXEC_TABLE_READ_PRIMARY !== "false";
const DUAL_WRITE = import.meta.env.VITE_EXEC_DUAL_WRITE !== "false";
const LEGACY_FALLBACK_READ = import.meta.env.VITE_EXEC_LEGACY_FALLBACK_READ !== "false";

function phaseRank(status: string): number {
  switch (status) {
    case "qa_passed":
      return 4;
    case "submitted":
      return 3;
    case "qa_failed":
      return 2;
    case "in_progress":
      return 1;
    default:
      return 0;
  }
}

function executionProgressScore(execution: JobSpecExecution): number {
  const phaseScore = execution.phases.reduce((acc, phase) => acc + phaseRank(phase.status), 0);
  const completedBonus = execution.allPhasesComplete ? 1000 : 0;
  return completedBonus + phaseScore;
}

function pickMostAdvancedExecution(
  primary: JobSpecExecution | null,
  secondary: JobSpecExecution | null,
): JobSpecExecution | null {
  if (!primary) return secondary;
  if (!secondary) return primary;
  return executionProgressScore(primary) >= executionProgressScore(secondary) ? primary : secondary;
}

function metadataExecution(raw: unknown): JobSpecExecution | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  const specRef = (obj.specRef as Record<string, unknown> | undefined) ?? {};
  const jobId = String(obj.jobId ?? obj.job_id ?? "").trim();
  const specId = String(specRef.specId ?? specRef.spec_id ?? "").trim();
  const versionNumber = Number(specRef.versionNumber ?? specRef.version_number ?? 0);
  if (!jobId || !specId || Number.isNaN(versionNumber)) return null;
  return {
    jobId,
    specRef: { specId, versionNumber },
    phases: Array.isArray(obj.phases) ? (obj.phases as JobSpecExecution["phases"]) : [],
    allPhasesComplete: Boolean(obj.allPhasesComplete ?? obj.all_phases_complete ?? false),
  };
}

async function readLegacyMetadata(jobId: string): Promise<JobSpecExecution | null> {
  const { data, error } = await supabase
    .from("jobs")
    .select("metadata")
    .eq("id", jobId)
    .maybeSingle();
  if (error || !data) return null;
  const metadata = (data.metadata ?? {}) as Record<string, unknown>;
  return (
    metadataExecution(metadata.spec_execution) ??
    metadataExecution(metadata.specExecution) ??
    metadataExecution(metadata.job_spec_execution) ??
    metadataExecution(metadata.jobSpecExecution)
  );
}

async function writeLegacyMetadata(jobId: string, execution: JobSpecExecution): Promise<void> {
  const { data, error } = await supabase
    .from("jobs")
    .select("metadata")
    .eq("id", jobId)
    .maybeSingle();
  if (error || !data) return;
  const metadata = { ...((data.metadata ?? {}) as Record<string, unknown>) };
  metadata.spec_execution = execution;
  metadata.specExecution = execution;
  metadata.job_spec_execution = execution;
  metadata.jobSpecExecution = execution;
  await supabase.from("jobs").update({ metadata }).eq("id", jobId);
}

async function writeTables(execution: JobSpecExecution): Promise<void> {
  const rootStatus = execution.allPhasesComplete
    ? "qa_passed"
    : execution.phases.some((p) => p.status === "submitted")
      ? "submitted"
      : execution.phases.some((p) => p.status === "in_progress" || p.status === "qa_failed")
        ? "in_progress"
        : "not_started";
  const rootFull = {
    job_id: execution.jobId,
    spec_ref: execution.specRef.specId,
    spec_version: String(execution.specRef.versionNumber),
    status: rootStatus,
    metadata: execution,
    updated_at: new Date().toISOString(),
  };
  const rootSlim = {
    job_id: execution.jobId,
    spec_ref: execution.specRef.specId,
    spec_version: String(execution.specRef.versionNumber),
    status: rootStatus,
    metadata: execution,
    updated_at: new Date().toISOString(),
  };
  const rootMinimal = {
    job_id: execution.jobId,
    updated_at: new Date().toISOString(),
  };
  const rootBare = {
    job_id: execution.jobId,
    spec_ref: execution.specRef.specId,
    spec_version: String(execution.specRef.versionNumber),
    status: rootStatus,
    metadata: execution,
  };
  const rootJobOnly = {
    job_id: execution.jobId,
  };

  let rootWrite = await supabase
    .from("job_spec_executions")
    .upsert(rootFull, { onConflict: "job_id,spec_ref,spec_version" })
    .select("id")
    .maybeSingle();

  if (rootWrite.error && /all_phases_complete|payload/i.test(rootWrite.error.message ?? "")) {
    rootWrite = await supabase
      .from("job_spec_executions")
      .upsert(rootSlim, { onConflict: "job_id,spec_ref,spec_version" })
      .select("id")
      .maybeSingle();
  }

  if (rootWrite.error && /payload|metadata|status/i.test(rootWrite.error.message ?? "")) {
    rootWrite = await supabase
      .from("job_spec_executions")
      .upsert(rootBare, { onConflict: "job_id,spec_ref,spec_version" })
      .select("id")
      .maybeSingle();
  }

  if (rootWrite.error && /spec_ref|spec_version|on_conflict/i.test(rootWrite.error.message ?? "")) {
    rootWrite = await supabase
      .from("job_spec_executions")
      .upsert(rootMinimal, { onConflict: "job_id" })
      .select("id")
      .maybeSingle();
  }

  if (rootWrite.error && /updated_at/i.test(rootWrite.error.message ?? "")) {
    rootWrite = await supabase
      .from("job_spec_executions")
      .upsert(rootJobOnly, { onConflict: "job_id" })
      .select("id")
      .maybeSingle();
  }

  if (rootWrite.error || !rootWrite.data?.id) {
    if (rootWrite.error) console.warn("[ExecutionRepository] table write root failed:", rootWrite.error.message);
    return;
  }
  const executionId = rootWrite.data.id as string;

  const phases = execution.phases.map((phase) => ({
    execution_id: executionId,
    phase_name: phase.phaseName,
    status: phase.status,
    completed_at: phase.completedAt ?? null,
    completed_by: phase.completedBy ?? null,
    qa_reviewed_at: phase.qaReviewedAt ?? null,
    qa_reviewed_by: phase.qaReviewer ?? null,
    qa_notes: phase.qaFailReason ?? null,
    updated_at: new Date().toISOString(),
  }));
  const phaseWrite = await supabase
    .from("job_spec_execution_phases")
    .upsert(phases, { onConflict: "execution_id,phase_name" })
    .select("id,phase_name");
  if (phaseWrite.error || !phaseWrite.data) {
    if (phaseWrite.error) console.warn("[ExecutionRepository] table write phases failed:", phaseWrite.error.message);
    return;
  }
  const phaseIdByName = new Map<string, string>();
  for (const row of phaseWrite.data as Array<{ id: string; phase_name: string }>) {
    phaseIdByName.set(row.phase_name, row.id);
  }

  const checks = execution.phases.flatMap((phase) => {
    const phaseId = phaseIdByName.get(phase.phaseName);
    if (!phaseId) return [];
    return phase.checklist.map((item) => ({
      phase_id: phaseId,
      checklist_item_id: item.itemId,
      checklist_label: item.itemId,
      checked: item.checked,
      checked_by: item.checked ? "installer" : null,
      checked_at: item.checkedAt ?? null,
      updated_at: new Date().toISOString(),
    }));
  });
  if (checks.length > 0) {
    const { error: checksError } = await supabase
      .from("job_spec_execution_checks")
      .upsert(checks, { onConflict: "phase_id,checklist_item_id" });
    if (checksError) console.warn("[ExecutionRepository] table write checks failed:", checksError.message);
  }

  const photos = execution.phases.flatMap((phase) => {
    const phaseId = phaseIdByName.get(phase.phaseName);
    if (!phaseId) return [];
    return phase.photos.map((photo) => ({
      phase_id: phaseId,
      photo_type: photo.photoType,
      storage_path: photo.url,
      attachment_id:
        typeof (photo as { attachmentId?: unknown }).attachmentId === "string"
          ? ((photo as { attachmentId?: string }).attachmentId ?? null)
          : null,
      uploaded_by: "installer",
      uploaded_at: photo.uploadedAt,
      updated_at: new Date().toISOString(),
    }));
  });
  if (photos.length > 0) {
    const { error: photosError } = await supabase
      .from("job_spec_execution_photos")
      .upsert(photos, { onConflict: "phase_id,photo_type" });
    if (photosError) console.warn("[ExecutionRepository] table write photos failed:", photosError.message);
  }
}

async function readTables(jobId: string): Promise<JobSpecExecution | null> {
  const { data, error } = await supabase
    .from("job_spec_executions")
    .select("metadata")
    .eq("job_id", jobId)
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error || !data) {
    if (error && !/metadata/i.test(error.message ?? "")) {
      console.warn("[ExecutionRepository] table read failed:", error.message);
    }
    return null;
  }
  return metadataExecution((data as { metadata?: unknown }).metadata);
}

export function createExecutionRepository(options: RepositoryOptions): ExecutionRepository {
  const ensureCurrent = async (jobId: string, bound?: BoundJobSpec): Promise<JobSpecExecution> => {
    const cached = options.getCachedExecution(jobId);
    const cachedExecution = cached ?? null;
    let loaded: JobSpecExecution | null = null;
    if (READ_TABLES_PRIMARY) loaded = await readTables(jobId);
    loaded = pickMostAdvancedExecution(loaded, cachedExecution);
    if (!loaded && LEGACY_FALLBACK_READ) loaded = await readLegacyMetadata(jobId);
    loaded = pickMostAdvancedExecution(loaded, cachedExecution);

    if (!loaded) {
      if (!bound) throw new Error("Execution not found and no bound spec provided.");
      const { execution } = ensureExecution([], jobId, bound);
      loaded = execution;
    }
    options.setCachedExecution(loaded);
    return loaded;
  };

  const persist = async (next: JobSpecExecution): Promise<JobSpecExecution> => {
    options.setCachedExecution(next);
    if (DUAL_WRITE) {
      void writeLegacyMetadata(next.jobId, next);
    }
    void writeTables(next);
    return next;
  };

  return {
    async getExecution(jobId: string, bound?: BoundJobSpec): Promise<JobSpecExecution | null> {
      try {
        return await ensureCurrent(jobId, bound);
      } catch {
        return null;
      }
    },

    async toggleChecklist(input: ToggleCheckInput): Promise<JobSpecExecution> {
      const current = await ensureCurrent(input.jobId, input.bound);
      const next = toggleChecklist(current, input.phaseName, input.itemId, input.checked, new Date());
      return persist(next);
    },

    async uploadRequiredPhoto(input: UploadProofInput): Promise<JobSpecExecution> {
      const current = await ensureCurrent(input.jobId, input.bound);
      let attachmentId: string | undefined;
      let attachmentPath: string | undefined;
      let uploadedAt: string | undefined;

      if (input.file) {
        try {
          const attachment = await uploadAttachment(
            input.file,
            "job",
            input.jobId,
            "photo",
            input.actor,
            `Execution photo: ${input.phaseName}/${input.photoType}`,
          );
          attachmentId = attachment.id;
          attachmentPath = attachment.filePath;
          uploadedAt = attachment.createdAt;
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          // Non-blocking fallback for envs where the configured bucket hasn't been provisioned yet.
          if (!/bucket not found/i.test(message)) {
            throw error;
          }
          console.warn("[ExecutionRepository] photo upload skipped (bucket missing), recording proof-only state.");
        }
      }

      const nextBase = recordPhoto(current, input.phaseName, input.photoType, new Date());
      const next = {
        ...nextBase,
        phases: nextBase.phases.map((phase) => {
          if (phase.phaseName !== input.phaseName) return phase;
          return {
            ...phase,
            photos: phase.photos.map((photo) =>
              photo.photoType === input.photoType
                ? {
                    ...photo,
                    url: attachmentPath ?? photo.url,
                    uploadedAt: uploadedAt ?? photo.uploadedAt,
                    ...(attachmentId ? { attachmentId } : {}),
                  }
                : photo,
            ),
          };
        }),
      };
      return persist(next);
    },

    async submitPhase(input: SubmitPhaseInput): Promise<JobSpecExecution> {
      const current = await ensureCurrent(input.jobId, input.bound);
      if (!input.bound) throw new Error("Bound spec required to submit a phase.");
      const spec = resolveBoundSpec(input.bound);
      if (!spec) throw new Error("Bound spec not found in registry.");
      const kit = options.getMaterialKit?.(input.jobId);
      const submitResult = submitPhase(
        current,
        spec,
        input.phaseName,
        input.actor,
        input.at ?? new Date(),
        kit,
      );
      if (submitResult.ok === false) {
        throw new Error(submitResult.reason);
      }
      return persist(submitResult.execution as JobSpecExecution);
    },

    async qaPass(jobId: string, phaseName: JobSpecPhaseName, actor: string): Promise<JobSpecExecution> {
      const current = await ensureCurrent(jobId);
      const reviewResult = qaPassPhase(current, phaseName, actor, new Date());
      if (reviewResult.ok === false) {
        throw new Error(reviewResult.reason);
      }
      return persist(reviewResult.execution);
    },

    async qaFail(jobId: string, phaseName: JobSpecPhaseName, actor: string, reason: string): Promise<JobSpecExecution> {
      const current = await ensureCurrent(jobId);
      const reviewResult = qaFailPhase(current, phaseName, actor, reason, new Date());
      if (reviewResult.ok === false) {
        throw new Error(reviewResult.reason);
      }
      return persist(reviewResult.execution);
    },
  };
}
